/**
 * useVehicleDetails - Dados do modal de detalhes do veículo
 *
 * Expõe:
 *  - `useVehicleDetails`  → RPC get_vehicle_details (dados + contadores + listas)
 *  - `useVehicleTimeline` → RPC list_vehicle_timeline (andamentos + histórico)
 *  - `useVehicleNotes`    → mutations de andamento (criar/editar/excluir) e upload de anexos
 *
 * Toda leitura/escrita passa por RPCs SECURITY DEFINER (nunca SELECT direto),
 * exceto o registro de anexos, protegido por RLS de tenant.
 *
 * @version 1.0.3
 * RELEASE: 2026-10-02 v1.0.3 - useVehicleDetails ganha refetchInterval de 30 s enquanto
 *          a ficha está aberta (garantia extra ao tempo real useVehicleRecordRealtime).
 * RELEASE: 2026-10-02 v1.0.2 - Campo status_id exposto no objeto vehicle (o RPC
 *          get_vehicle_details já entrega a coluna via to_jsonb; header do modal
 *          exibe a etiqueta de Status do veículo com VehicleStatusBadge).
 * RELEASE: 2026-08-08 v1.0.1 - Sanitiza o nome do arquivo ao montar o caminho no Storage
 *          (acentos/espaços geravam erro "Invalid key" no upload de anexos do andamento)
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial (modal de detalhes do veículo)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import type { VehicleType } from '@/hooks/useCustomerVehicles';
import { sanitizeStorageFileName } from '@/lib/storageFileName';

const logger = createLogger('useVehicleDetails');

/** Bucket público já usado por outros módulos para anexos gerais */
const ATTACHMENTS_BUCKET = 'anexos';

/**
 * Sanitiza o nome do arquivo para uso como chave no Supabase Storage.
 * Mesmo padrão já adotado em useTaskAttachments/useDealAttachments:
 * remove acentos, troca espaços por "_" e elimina caracteres inválidos.
 */
// v1.x: delegado ao utilitário central (remove emojis/acentos e evita
// o erro "Invalid key" do Supabase Storage). O nome original permanece no banco.
function sanitizeFileName(fileName: string): string {
  return sanitizeStorageFileName(fileName);
}



/** Dados completos do veículo devolvidos pela RPC */
export interface VehicleDetailsVehicle {
  id: string;
  tenant_id: string;
  vehicle_type: VehicleType;
  category_id: string | null;
  category_name: string | null;
  /** IDs relacionais FIPE (vindos de to_jsonb da linha customer_vehicles) */
  brand_id?: string | null;
  model_id?: string | null;
  year_code?: string | null;
  brand_name: string | null;
  model_name: string | null;
  year: number | null;
  fuel: string | null;
  color: string | null;
  plate: string | null;
  renavam: string | null;
  chassis: string | null;
  fipe_code: string | null;
  fipe_value: number | null;
  notes: string | null;
  is_active: boolean;
  customer_id: string | null;
  contact_id: string | null;
  customer_name: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  /** v1.1.0 — dados complementares exibidos no card de identidade da sidebar */
  customer_type?: 'pf' | 'pj' | null;
  customer_document?: string | null;
  contact_email?: string | null;
  contact_avatar_url?: string | null;
  contact_channel_ids?: Record<string, unknown> | null;
  contact_metadata?: Record<string, unknown> | null;
  plate_synced_at: string | null;
  /** v1.0.2: status do veículo (id de vehicle_statuses; 'active' é o padrão) */
  status_id?: string | null;
  fines_synced_at: string | null;
  fines_total_open: number | null;
  created_at: string;
  updated_at: string;
}

/** Contadores das abas */
export interface VehicleDetailsCounts {
  deals: number;
  contracts: number;
  tasks: number;
  /** v1.2.0: total de conversas do contato vinculado (badge da aba Chat) */
  conversations: number;
  fines: number;
  notes: number;
  /** v1.4.0: vistorias em aberto (badge da aba Vistoria) */
  inspections?: number;
}

/** Negócio vinculado ao veículo */
export interface VehicleLinkedDeal {
  id: string;
  title: string;
  value: number | null;
  status: string;
  phase_name: string | null;
  phase_color: string | null;
  created_at: string;
}

/** Contrato vinculado ao veículo */
export interface VehicleLinkedContract {
  id: string;
  title: string | null;
  contract_number: string | null;
  status: string;
  created_at: string;
}

/** Tarefa vinculada ao veículo */
export interface VehicleLinkedTask {
  id: string;
  number: number;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assigned_to: string | null;
  assigned_name: string | null;
  assigned_avatar: string | null;
  created_at: string;
}

/** Payload completo da RPC get_vehicle_details */
export interface VehicleDetailsPayload {
  vehicle: VehicleDetailsVehicle;
  counts: VehicleDetailsCounts;
  deals: VehicleLinkedDeal[];
  contracts: VehicleLinkedContract[];
  tasks: VehicleLinkedTask[];
}

/** Anexo de um andamento */
export interface VehicleNoteAttachment {
  id: string;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  mime_type: string | null;
  storage_path: string;
  public_url: string | null;
}

/** Item da timeline (andamento do usuário ou movimentação automática) */
export interface VehicleTimelineItem {
  kind: 'note' | 'activity' | 'financeiro';
  id: string;
  created_at: string;
  updated_at?: string;
  author_id: string | null;
  author_name: string | null;
  author_avatar: string | null;
  /** kind = note */
  content?: string;
  content_html?: string | null;
  attachments?: VehicleNoteAttachment[];
  /** kind = activity */
  action?: string;
  changes?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  /** kind = financeiro (cobrança ou assinatura vinculada ao veículo) */
  entity?: 'cobranca' | 'assinatura';
  descricao?: string | null;
  valor_total?: number | null;
  status?: string | null;
  data_vencimento?: string | null;
  proximo_vencimento?: string | null;
  frequencia?: string | null;
  ciclo_numero?: number | null;
}

/** Hook de dados gerais do veículo */
export function useVehicleDetails(vehicleId: string | null, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['vehicle-details', tenantId, vehicleId],
    enabled: !!vehicleId && !!tenantId && enabled,
    staleTime: 15_000,
    // v1.0.3: atualização periódica com a ficha aberta (garantia ao Realtime)
    refetchInterval: enabled ? 30_000 : false,
    queryFn: async (): Promise<VehicleDetailsPayload> => {
      const { data, error } = await (supabase.rpc as any)('get_vehicle_details', {
        p_vehicle_id: vehicleId,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao carregar detalhes do veículo', { error: error.message });
        throw error;
      }
      return data as VehicleDetailsPayload;
    },
  });

  return {
    data: query.data ?? null,
    vehicle: query.data?.vehicle ?? null,
    counts: query.data?.counts ?? null,
    deals: query.data?.deals ?? [],
    contracts: query.data?.contracts ?? [],
    tasks: query.data?.tasks ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Hook da timeline (andamentos + movimentações) */
export function useVehicleTimeline(vehicleId: string | null, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['vehicle-timeline', tenantId, vehicleId],
    enabled: !!vehicleId && !!tenantId && enabled,
    staleTime: 10_000,
    // v1.1.0: além dos itens, a RPC devolve `labels` (UUID → nome amigável)
    queryFn: async (): Promise<{
      items: VehicleTimelineItem[];
      labels: Record<string, string>;
    }> => {
      const { data, error } = await (supabase.rpc as any)('list_vehicle_timeline', {
        p_vehicle_id: vehicleId,
        p_limit: 200,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao carregar timeline do veículo', { error: error.message });
        throw error;
      }
      return {
        items: ((data as any)?.items ?? []) as VehicleTimelineItem[],
        labels: ((data as any)?.labels ?? {}) as Record<string, string>,
      };
    },
  });

  return {
    items: query.data?.items ?? [],
    labels: query.data?.labels ?? {},
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

/** Hook de escrita dos andamentos */
export function useVehicleNotes(vehicleId: string | null) {
  const { currentTenant, user } = useAuth();
  const tenantId = currentTenant?.id ?? null;
  const queryClient = useQueryClient();

  /** Invalida timeline, contadores e a aba de anexos */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['vehicle-timeline', tenantId, vehicleId] });
    queryClient.invalidateQueries({ queryKey: ['vehicle-details', tenantId, vehicleId] });
    queryClient.invalidateQueries({ queryKey: ['vehicle-activity-attachments', vehicleId] });
  };

  /**
   * Remove um anexo: exclui o registro via RPC (que devolve o storage_path)
   * e, em seguida, apaga o arquivo do bucket. Mesmo padrão de tarefas.
   */
  const removeAttachmentById = async (attachmentId: string) => {
    const { data, error } = await (supabase.rpc as any)('delete_vehicle_attachment', {
      p_attachment_id: attachmentId,
      p_tenant_hint: tenantId,
    });
    if (error) throw error;

    const storagePath = data as string | null;
    if (storagePath) {
      const { error: storageError } = await supabase.storage
        .from(ATTACHMENTS_BUCKET)
        .remove([storagePath]);
      if (storageError) {
        // Falha no storage não invalida a exclusão lógica — apenas registra
        logger.warn('Falha ao remover arquivo do storage', {
          storagePath,
          error: storageError.message,
        });
      }
    }
  };

  /** Cria/edita um andamento, envia novos anexos e remove os marcados */
  const saveNote = useMutation({
    mutationFn: async (input: {
      content: string;
      contentHtml?: string | null;
      noteId?: string | null;
      files?: File[];
      /** IDs de anexos existentes marcados para exclusão durante a edição */
      removeAttachmentIds?: string[];
      /** RELEASE: 2026-09-12 - pessoas mencionadas já na criação do andamento */
      mentionedUserIds?: string[];
    }) => {
      if (!vehicleId) throw new Error('Veículo não informado');

      const { data, error } = await (supabase.rpc as any)('upsert_vehicle_note', {
        p_vehicle_id: vehicleId,
        p_content: input.content,
        p_content_html: input.contentHtml ?? null,
        p_note_id: input.noteId ?? null,
        // RELEASE: 2026-09-12 - menções notificadas de forma atômica pela RPC
        p_mentioned_user_ids: input.mentionedUserIds?.length
          ? input.mentionedUserIds
          : null,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;

      const noteId = data as string;

      // Exclusão dos anexos marcados durante a edição
      for (const attachmentId of input.removeAttachmentIds ?? []) {
        await removeAttachmentById(attachmentId);
      }

      // Upload dos anexos (bucket público "anexos")
      // O nome original é preservado no registro; apenas a chave do Storage é sanitizada.
      for (const file of input.files ?? []) {
        const safeName = sanitizeFileName(file.name);
        const path = `veiculos/${tenantId}/${vehicleId}/${noteId}/${Date.now()}-${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from(ATTACHMENTS_BUCKET)
          .upload(path, file, { upsert: false });
        if (uploadError) {
          logger.error('Falha no upload de anexo do veículo', {
            fileName: file.name,
            error: uploadError.message,
          });
          throw new Error(`Falha ao enviar o anexo "${file.name}": ${uploadError.message}`);
        }
        const { data: publicData } = supabase.storage.from(ATTACHMENTS_BUCKET).getPublicUrl(path);

        const { error: insertError } = await (supabase as any).from('vehicle_attachments').insert({
          tenant_id: tenantId,
          vehicle_id: vehicleId,
          vehicle_note_id: noteId,
          file_name: file.name,
          file_type: file.type?.startsWith('image/') ? 'image' : 'file',
          file_size: file.size,
          mime_type: file.type,
          storage_path: path,
          public_url: publicData?.publicUrl ?? null,
          uploaded_by: user?.id ?? null,
        });
        if (insertError) throw insertError;
      }

      return noteId;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Andamento registrado');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  /** Exclui um andamento (autor ou admin) */
  const deleteNote = useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await (supabase.rpc as any)('delete_vehicle_note', {
        p_note_id: noteId,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Andamento excluído');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  /** Exclui um anexo isolado (usado pela aba "Anexos") */
  const deleteAttachment = useMutation({
    mutationFn: async (attachmentId: string) => {
      await removeAttachmentById(attachmentId);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Anexo excluído');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return { saveNote, deleteNote, deleteAttachment };
}

